import { Injectable, inject } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Performance, trace, PerformanceTrace } from '@angular/fire/performance';
import { FirebaseService } from './firebase.service';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  debounceTime,
  from,
  interval,
  lastValueFrom,
  mergeMap,
  startWith,
  switchMap,
  take,
  takeUntil,
  throwError,
  timeout
} from 'rxjs';
import * as _ from 'lodash';
import { Analytics, logEvent, setUserId, setDefaultEventParameters } from '@angular/fire/analytics';
import { dbConfig } from '../_configs/db.config';
import { Collection } from '../_types/collection.type';
import { StorageService } from './storage.service';
import { environment } from '../../environments/environment';
import { FbEntity, JsEntity } from '../_types/entity.type';
import { FbEmail, JsEmail } from '../_interfaces/email.interface';

/**
 * IndexDB Error Types for categorized error handling
 */
enum IndexDbErrorType {
  STORE_NOT_FOUND = 'STORE_NOT_FOUND',
  DATABASE_CORRUPTED = 'DATABASE_CORRUPTED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  QUOTA_EXCEEDED = 'QUOTA_EXCEEDED',
  VERSION_ERROR = 'VERSION_ERROR',
  UNKNOWN = 'UNKNOWN'
}

/**
 * IndexedDB Service - Local cache layer for email templates
 *
 * This service provides a local IndexedDB cache for email templates.
 * All other collections (users, invitees, assets) are managed by FirestoreStoreService.
 *
 * Key Features:
 * - Robust error handling with automatic recovery
 * - Store existence validation before operations
 * - Proper async/await sequencing
 * - Health checks on initialization
 * - Safe cleanup on logout/errors
 */
@Injectable({
  providedIn: 'root'
})
export class IndexDbService {

  // Only 'emails' store is used - users, invitees, assets use FirestoreStoreService
  private readonly STORES: Collection[] = ['emails'];
  private readonly DB_NAME = dbConfig.name;
  private readonly OPERATION_TIMEOUT = 10000; // 10 seconds timeout for operations

  // Observable state
  emailSubject$ = new BehaviorSubject<JsEmail[] | null>(null);
  emails$: Observable<JsEmail[] | null> = this.emailSubject$.asObservable();

  // Internal state
  private fetchTriggers: { [key: string]: Subject<void> } = {
    emails: new Subject<void>()
  };
  private latestUpdatedAts: { [key: string]: Date } = {
    emails: new Date(2000, 0, 1)
  };
  private isInitialized = false;
  private isRecovering = false;
  private initializationPromise: Promise<void> | null = null;
  private idbPerformanceTrace: PerformanceTrace;
  unSubscribe = new Subject<void>();

  constructor(
    private dbService: NgxIndexedDBService,
    private fbService: FirebaseService,
    private ss: StorageService,
    private performance: Performance = inject(Performance),
    private analytics: Analytics = inject(Analytics)
  ) {
    this.setupAnalytics();
    this.idbPerformanceTrace = trace(this.performance, 'idbServiceService');
    this.idbPerformanceTrace.putAttribute('user', this.fbService.getCurrentUserId() || 'UNKNOWN');
    this.idbPerformanceTrace.putAttribute('version', environment.version);
  }

  private setupAnalytics(): void {
    setUserId(this.analytics, this.fbService.getCurrentUserId());
    setDefaultEventParameters({
      user: this.fbService.getCurrentUserId(),
      version: environment.version
    });
    logEvent(this.analytics, 'idb_service', {
      user: this.fbService.getCurrentUserId(),
      version: environment.version
    });
  }

  // ═══════════════════════════════════════════════════════════════
  // DATABASE HEALTH & VALIDATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Check if the IndexedDB database is healthy and all required stores exist
   */
  async checkDatabaseHealth(): Promise<boolean> {
    try {
      // Open the database and verify stores
      const db = await this.openDatabase();
      if (!db) {
        console.warn('[IndexDB] Database could not be opened');
        return false;
      }

      // Check if all required stores exist
      for (const storeName of this.STORES) {
        if (!db.objectStoreNames.contains(storeName)) {
          console.warn(`[IndexDB] Missing store: ${storeName}`);
          db.close();
          return false;
        }
      }

      db.close();
      return true;
    } catch (error) {
      console.error('[IndexDB] Health check failed:', error);
      return false;
    }
  }

  /**
   * Open the IndexedDB database directly for health checks
   */
  private openDatabase(): Promise<IDBDatabase | null> {
    return new Promise((resolve) => {
      try {
        const request = indexedDB.open(this.DB_NAME);

        request.onerror = () => {
          console.error('[IndexDB] Failed to open database for health check');
          resolve(null);
        };

        request.onsuccess = () => {
          resolve(request.result);
        };

        // If upgrade is needed, we need to let ngx-indexed-db handle it
        request.onupgradeneeded = () => {
          console.log('[IndexDB] Database upgrade needed - closing for ngx-indexed-db to handle');
          request.result.close();
          resolve(null);
        };
      } catch (error) {
        console.error('[IndexDB] Error opening database:', error);
        resolve(null);
      }
    });
  }

  /**
   * Check if a specific store exists and is accessible
   */
  private async isStoreAccessible(storeName: Collection): Promise<boolean> {
    try {
      // Try a simple operation to verify store is accessible
      await lastValueFrom(
        this.dbService.count(storeName).pipe(
          timeout(this.OPERATION_TIMEOUT)
        )
      );
      return true;
    } catch (error) {
      console.warn(`[IndexDB] Store '${storeName}' is not accessible:`, error);
      return false;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ERROR HANDLING & RECOVERY
  // ═══════════════════════════════════════════════════════════════

  /**
   * Categorize IndexedDB errors for appropriate handling
   */
  private categorizeError(error: any): IndexDbErrorType {
    const message = error?.message?.toLowerCase() || '';
    const name = error?.name || '';

    if (name === 'NotFoundError' || message.includes('objectstore') || message.includes('not found')) {
      return IndexDbErrorType.STORE_NOT_FOUND;
    }
    if (message.includes('version') || name === 'VersionError') {
      return IndexDbErrorType.VERSION_ERROR;
    }
    if (message.includes('quota') || name === 'QuotaExceededError') {
      return IndexDbErrorType.QUOTA_EXCEEDED;
    }
    if (message.includes('transaction') || name === 'TransactionInactiveError') {
      return IndexDbErrorType.TRANSACTION_FAILED;
    }
    if (message.includes('corrupt') || message.includes('invalid')) {
      return IndexDbErrorType.DATABASE_CORRUPTED;
    }

    return IndexDbErrorType.UNKNOWN;
  }

  /**
   * Handle IndexedDB errors with appropriate recovery strategies
   */
  private async handleError(error: any, operation: string): Promise<void> {
    const errorType = this.categorizeError(error);
    console.error(`[IndexDB] Error during ${operation}:`, { errorType, error });

    logEvent(this.analytics, 'idb_error', {
      operation,
      errorType,
      message: error?.message
    });

    switch (errorType) {
      case IndexDbErrorType.STORE_NOT_FOUND:
      case IndexDbErrorType.DATABASE_CORRUPTED:
      case IndexDbErrorType.VERSION_ERROR:
        // These require database recreation
        await this.attemptRecovery();
        break;

      case IndexDbErrorType.QUOTA_EXCEEDED:
        // Try to clear some space
        console.warn('[IndexDB] Storage quota exceeded - attempting to free space');
        await this.clearAllStores();
        break;

      case IndexDbErrorType.TRANSACTION_FAILED:
        // Transaction failures may be transient - log but don't auto-recover
        console.warn('[IndexDB] Transaction failed - may be transient');
        break;

      default:
        console.error('[IndexDB] Unknown error type - attempting recovery');
        await this.attemptRecovery();
    }
  }

  /**
   * Attempt to recover from database errors
   * Uses a lock to prevent multiple concurrent recovery attempts
   */
  private async attemptRecovery(): Promise<void> {
    if (this.isRecovering) {
      console.log('[IndexDB] Recovery already in progress, skipping...');
      return;
    }

    this.isRecovering = true;
    console.log('[IndexDB] Attempting database recovery...');

    try {
      // Step 1: Stop all active operations
      this.stopSubscription();

      // Step 2: Reset internal state
      this.resetInternalState();

      // Step 3: Delete the database
      await this.deleteDatabase();

      // Step 4: Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 500));

      // Step 5: Reload page to reinitialize with fresh database
      console.log('[IndexDB] Recovery complete - reloading page...');
      window.location.reload();
    } catch (recoveryError) {
      console.error('[IndexDB] Recovery failed:', recoveryError);
      // As last resort, prompt user
      this.promptUserForManualClear();
    } finally {
      this.isRecovering = false;
    }
  }

  /**
   * Prompt user to manually clear browser data
   */
  private promptUserForManualClear(): void {
    const message = 'Database error detected. Please clear your browser data for this site:\n\n' +
      '1. Open browser settings\n' +
      '2. Go to Privacy & Security > Clear browsing data\n' +
      '3. Select "Cookies and site data" and "Cached images and files"\n' +
      '4. Click Clear data\n' +
      '5. Refresh this page';

    alert(message);
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION & RESYNC
  // ═══════════════════════════════════════════════════════════════

  /**
   * Resync - triggered when app needs a clean start (e.g., version update)
   */
  async resync(): Promise<void> {
    console.log('[IndexDB] RESYNCING...');
    this.ss.resetUserSessionStorage();

    try {
      // Verify database is healthy before proceeding
      const isHealthy = await this.checkDatabaseHealth();
      if (!isHealthy) {
        console.warn('[IndexDB] Database unhealthy during resync - attempting recovery');
        await this.attemptRecovery();
        return; // Recovery will reload the page
      }

      await this.resyncEmailsStore();
      await this.updateObservablesFromIdb();
      console.log('[IndexDB] Resync complete');
    } catch (error) {
      console.error('[IndexDB] Error during resync:', error);
      await this.handleError(error, 'resync');
    }
  }

  /**
   * Initialize the IndexedDB service
   * Called on app start to set up real-time sync from Firestore
   */
  async initialise(): Promise<void> {
    const localUser = this.ss.getUser();
    const localUserId = localUser?.uid;

    if (!localUserId) {
      console.log('[IndexDB] No user logged in, skipping initialization');
      return;
    }

    // Prevent concurrent initialization
    if (this.initializationPromise) {
      console.log('[IndexDB] Initialization already in progress, waiting...');
      return this.initializationPromise;
    }

    if (this.isInitialized) {
      console.log('[IndexDB] Already initialized, skipping');
      return;
    }

    this.initializationPromise = this.doInitialize();

    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Internal initialization logic
   */
  private async doInitialize(): Promise<void> {
    console.log('[IndexDB] Initializing...');

    try {
      // Step 1: Health check
      const isHealthy = await this.checkDatabaseHealth();
      if (!isHealthy) {
        console.warn('[IndexDB] Database unhealthy during init - attempting recovery');
        await this.attemptRecovery();
        return;
      }

      // Step 2: Verify emails store is accessible
      const storeAccessible = await this.isStoreAccessible('emails');
      if (!storeAccessible) {
        console.warn('[IndexDB] Emails store not accessible - attempting recovery');
        await this.attemptRecovery();
        return;
      }

      this.isInitialized = true;

      // Step 3: Get latest timestamps and set up collection listeners
      const collections = ['emails'] as Collection[];
      const initCollections = collections.map(collection =>
        this.getLatestCloudUpdatedAt(collection).then(timestamp => {
          console.log('[IndexDB] Latest timestamp for', collection, timestamp);
          this.latestUpdatedAts[collection] = timestamp;
          this.initialiseCollection(collection);
        })
      );

      await Promise.all(initCollections);

      // Step 4: Set up periodic sync check
      this.setupPeriodicSync(collections);

      // Step 5: Update observables from current IDB data
      await this.updateObservablesFromIdb();

      console.log('[IndexDB] Initialization complete');
    } catch (error) {
      console.error('[IndexDB] Initialization failed:', error);
      this.isInitialized = false;
      await this.handleError(error, 'initialise');
    }
  }

  /**
   * Set up periodic sync for offline recovery
   */
  private setupPeriodicSync(collections: Collection[]): void {
    let lastCheckTime = new Date();
    let initialCheckDone = false;

    interval(1000 * 60)
      .pipe(startWith(0), takeUntil(this.unSubscribe))
      .subscribe(() => {
        const currentTime = new Date();
        const timeDiff = currentTime.getTime() - lastCheckTime.getTime();

        // Check for new items if more than 2 mins have passed or first check
        if (timeDiff > 1000 * 60 * 2 || !initialCheckDone) {
          console.log('[IndexDB] Periodic sync check');
          initialCheckDone = true;
          lastCheckTime = currentTime;
          collections.forEach(collection => {
            this.fetchTriggers[collection]?.next();
          });
        } else {
          lastCheckTime = currentTime;
        }
      });
  }

  /**
   * Initialize collection listener
   */
  initialiseCollection(collection: Collection): void {
    this.fetchTriggers[collection]?.pipe(
      switchMap(() => this.fetchLatestData(collection)),
      takeUntil(this.unSubscribe)
    ).subscribe();
  }

  /**
   * Fetch latest data from Firestore for a collection
   */
  fetchLatestData(collection: Collection): Observable<JsEntity[]> {
    console.log('[IndexDB] Fetching latest data for', collection);
    const baseObservable = this.fbService.getAllItems<FbEntity, JsEntity>(
      collection,
      this.latestUpdatedAts[collection]
    );

    return baseObservable.pipe(
      debounceTime(3000),
      mergeMap(async (items: JsEntity[]) => {
        console.log(`[IndexDB] Received ${items.length} items for ${collection}`);
        this.idbPerformanceTrace.incrementMetric(`INIT - ${collection}`, items.length);
        logEvent(this.analytics, 'init_firestore_read_' + collection, {
          count: items.length
        });

        if (items.length > 0) {
          try {
            await this.updateBulkSafe(collection, items);
            await this.updateObservablesFromIdb(collection);
          } catch (error) {
            console.error(`[IndexDB] Error updating ${collection}:`, error);
            await this.handleError(error, `fetchLatestData_${collection}`);
          }
        }

        const latestUpdatedItem = _.maxBy(items, 'cloudUpdatedAt');
        if (latestUpdatedItem?.cloudUpdatedAt &&
            latestUpdatedItem.cloudUpdatedAt > this.latestUpdatedAts[collection]) {
          this.latestUpdatedAts[collection] = latestUpdatedItem.cloudUpdatedAt as Date;
          this.fetchTriggers[collection].next();
        }

        return items;
      }),
      catchError(e => {
        console.error(`[IndexDB] Error fetching ${collection}:`, e);
        return throwError(() => e);
      })
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // STORE OPERATIONS (with safety checks)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Update observables from IndexedDB data
   */
  async updateObservablesFromIdb(entity: Collection | null = null): Promise<boolean> {
    try {
      if (entity === null || entity === 'emails') {
        const emails = await this.getAllSafe<JsEmail>('emails');
        console.log('[IndexDB] Loaded', emails.length, 'emails from IDB');
        this.emailSubject$.next(emails || []);
      }
      return true;
    } catch (error) {
      console.error('[IndexDB] Error updating observables:', error);
      await this.handleError(error, 'updateObservablesFromIdb');
      return false;
    }
  }

  /**
   * Resync emails store - clear and reload from Firestore
   */
  async resyncEmailsStore(): Promise<void> {
    try {
      // Verify store is accessible before proceeding
      const accessible = await this.isStoreAccessible('emails');
      if (!accessible) {
        throw new Error('Emails store not accessible');
      }

      await this.clearStoreObjectsSafe('emails');
      const items = await lastValueFrom(
        this.fbService.getAllItems<FbEmail, JsEmail>('emails').pipe(take(1))
      );
      await this.addBulkSafe('emails', items || []);

      console.log('[IndexDB] Resynced emails:', items?.length);
      this.idbPerformanceTrace.incrementMetric('RESYNC - emails', items?.length || 0);
      logEvent(this.analytics, 'resync_firestore_read_emails', {
        count: items?.length || 0
      });
    } catch (error) {
      console.error('[IndexDB] Error resyncing emails:', error);
      throw error;
    }
  }

  /**
   * Get the latest cloudUpdatedAt timestamp from a store
   */
  async getLatestCloudUpdatedAt(store: Collection): Promise<Date> {
    let latestDate = new Date(2000, 0, 1);

    try {
      const items = await this.getAllSafe(store);
      items.forEach((element: any) => {
        if (element.cloudUpdatedAt && element.cloudUpdatedAt > latestDate) {
          latestDate = element.cloudUpdatedAt;
        }
      });
    } catch (error) {
      console.warn(`[IndexDB] Error getting latest timestamp for ${store}:`, error);
      // Return default date on error
    }

    return latestDate;
  }

  // ═══════════════════════════════════════════════════════════════
  // SAFE CRUD OPERATIONS (with error handling)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Safe getAll with error handling
   */
  async getAllSafe<T extends JsEntity>(storeName: Collection): Promise<T[]> {
    try {
      return await lastValueFrom(
        this.dbService.getAll<T>(storeName).pipe(timeout(this.OPERATION_TIMEOUT))
      );
    } catch (error) {
      console.error(`[IndexDB] Error getting all from ${storeName}:`, error);
      await this.handleError(error, `getAll_${storeName}`);
      return [];
    }
  }

  /**
   * Safe addBulk with error handling
   */
  async addBulkSafe(storeName: Collection, values: JsEntity[]): Promise<any> {
    if (values.length === 0) return;

    try {
      return await lastValueFrom(
        this.dbService.bulkAdd(storeName, values).pipe(timeout(this.OPERATION_TIMEOUT))
      );
    } catch (error) {
      console.error(`[IndexDB] Error bulk adding to ${storeName}:`, error);
      await this.handleError(error, `addBulk_${storeName}`);
      throw error;
    }
  }

  /**
   * Safe updateBulk with error handling
   */
  async updateBulkSafe(storeName: Collection, values: JsEntity[]): Promise<any> {
    if (values.length === 0) return;

    try {
      return await lastValueFrom(
        this.dbService.bulkPut(storeName, values).pipe(timeout(this.OPERATION_TIMEOUT))
      );
    } catch (error) {
      console.error(`[IndexDB] Error bulk updating ${storeName}:`, error);
      await this.handleError(error, `updateBulk_${storeName}`);
      throw error;
    }
  }

  /**
   * Safe clearStoreObjects with error handling
   */
  async clearStoreObjectsSafe(storeName: Collection): Promise<boolean> {
    try {
      await lastValueFrom(
        this.dbService.clear(storeName).pipe(timeout(this.OPERATION_TIMEOUT))
      );
      return true;
    } catch (error) {
      console.error(`[IndexDB] Error clearing ${storeName}:`, error);
      // Don't trigger recovery for clear errors - just log and continue
      return false;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // LEGACY CRUD OPERATIONS (for backwards compatibility)
  // ═══════════════════════════════════════════════════════════════

  getEntityById(storeName: Collection, id: string | number): Observable<any> {
    return from(this.dbService.getByID(storeName, id));
  }

  async getAll<T extends JsEntity>(storeName: Collection): Promise<T[]> {
    return this.getAllSafe<T>(storeName);
  }

  async getByKey(storeName: Collection, key: any): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.getByKey(storeName, key));
    } catch (error) {
      console.error(`[IndexDB] Error getting by key from ${storeName}:`, error);
      return null;
    }
  }

  async getByIndex(storeName: Collection, index: string, key: any): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.getByIndex(storeName, index, key));
    } catch (error) {
      console.error(`[IndexDB] Error getting by index from ${storeName}:`, error);
      return null;
    }
  }

  async addBulk(storeName: Collection, values: JsEntity[]): Promise<any> {
    return this.addBulkSafe(storeName, values);
  }

  async add(storeName: Collection, value: JsEntity): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.add(storeName, value));
    } catch (error) {
      console.error(`[IndexDB] Error adding to ${storeName}:`, error);
      await this.handleError(error, `add_${storeName}`);
      throw error;
    }
  }

  async update(storeName: Collection, value: JsEntity): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.update(storeName, value));
    } catch (error) {
      console.error(`[IndexDB] Error updating ${storeName}:`, error);
      await this.handleError(error, `update_${storeName}`);
      throw error;
    }
  }

  async updateBulk(storeName: Collection, values: JsEntity[]): Promise<any> {
    return this.updateBulkSafe(storeName, values);
  }

  async getBulk(storeName: Collection, keys: any[]): Promise<any[]> {
    try {
      return await lastValueFrom(this.dbService.bulkGet(storeName, keys));
    } catch (error) {
      console.error(`[IndexDB] Error bulk getting from ${storeName}:`, error);
      return [];
    }
  }

  async delete(storeName: Collection, key: any): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.delete(storeName, key));
    } catch (error) {
      console.error(`[IndexDB] Error deleting from ${storeName}:`, error);
      // Don't trigger recovery for delete errors
      return null;
    }
  }

  async bulkDelete(storeName: Collection, keys: any[]): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.bulkDelete(storeName, keys));
    } catch (error) {
      console.error(`[IndexDB] Error bulk deleting from ${storeName}:`, error);
      return null;
    }
  }

  async count(storeName: Collection): Promise<number> {
    try {
      return await lastValueFrom(this.dbService.count(storeName));
    } catch (error) {
      console.error(`[IndexDB] Error counting ${storeName}:`, error);
      return 0;
    }
  }

  async clearStoreObjects(storeName: Collection): Promise<boolean> {
    return this.clearStoreObjectsSafe(storeName);
  }

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP & RESET
  // ═══════════════════════════════════════════════════════════════

  /**
   * Clear all stores - FIXED: properly awaits all operations
   */
  async clearAllStores(): Promise<boolean> {
    console.log('[IndexDB] Clearing all stores...');

    try {
      // Use Promise.all to properly await all clear operations
      const clearPromises = this.STORES.map(store =>
        this.clearStoreObjectsSafe(store).catch(err => {
          console.warn(`[IndexDB] Failed to clear store ${store}:`, err);
          return false;
        })
      );

      await Promise.all(clearPromises);
      console.log('[IndexDB] All stores cleared');
      return true;
    } catch (error) {
      console.error('[IndexDB] Error clearing all stores:', error);
      return false;
    }
  }

  /**
   * Delete the IndexedDB database
   */
  async deleteDatabase(): Promise<void> {
    console.log('[IndexDB] Deleting database...');

    try {
      // Try using ngx-indexed-db first
      await lastValueFrom(
        this.dbService.deleteDatabase().pipe(timeout(this.OPERATION_TIMEOUT))
      );
      console.log('[IndexDB] Database deleted via ngx-indexed-db');
    } catch (error) {
      console.warn('[IndexDB] ngx-indexed-db delete failed, trying native API:', error);

      // Fallback to native IndexedDB API
      try {
        await this.deleteDbNative(this.DB_NAME);
        console.log('[IndexDB] Database deleted via native API');
      } catch (nativeError) {
        console.error('[IndexDB] Native delete also failed:', nativeError);
        throw nativeError;
      }
    }
  }

  /**
   * Delete database using native IndexedDB API
   */
  private deleteDbNative(dbName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(dbName);

      request.onerror = () => {
        reject(new Error(`Failed to delete database: ${dbName}`));
      };

      request.onsuccess = () => {
        resolve();
      };

      request.onblocked = () => {
        console.warn('[IndexDB] Database deletion blocked - other connections open');
        // Still resolve after a timeout
        setTimeout(resolve, 1000);
      };
    });
  }

  /**
   * Reset internal state
   */
  private resetInternalState(): void {
    this.isInitialized = false;
    this.isRecovering = false;
    this.initializationPromise = null;
    this.latestUpdatedAts = {
      emails: new Date(2000, 0, 1)
    };
    this.emailSubject$.next(null);
  }

  /**
   * Stop all subscriptions
   */
  stopSubscription(): void {
    this.unSubscribe?.next();
    this.unSubscribe?.complete();
    this.unSubscribe = new Subject<void>();
  }

  /**
   * Full reset - call on logout
   * Properly sequences all cleanup operations
   */
  async fullReset(): Promise<void> {
    console.log('[IndexDB] FULL RESET starting...');

    // Step 1: Stop all subscriptions FIRST
    this.stopSubscription();

    // Step 2: Clear BehaviorSubjects
    this.emailSubject$.next(null);

    // Step 3: Try to delete the database
    try {
      await this.deleteDatabase();
      console.log('[IndexDB] Database deleted successfully');
    } catch (error) {
      console.warn('[IndexDB] Database delete failed, trying to clear stores:', error);

      // Fallback: try to clear stores individually
      try {
        await this.clearAllStores();
      } catch (clearError) {
        console.error('[IndexDB] Store clear also failed:', clearError);
        // Continue anyway - don't block logout
      }
    }

    // Step 4: Reset all internal state
    this.resetInternalState();

    // Step 5: Recreate unSubscribe subject for potential next session
    this.unSubscribe = new Subject<void>();

    console.log('[IndexDB] FULL RESET complete');
  }

  /**
   * Delete all IndexedDB databases for this origin
   * Used by auth interceptor for complete cleanup
   */
  static async deleteAllDatabases(): Promise<void> {
    if (!indexedDB?.databases) {
      console.warn('[IndexDB] indexedDB.databases() not supported');
      return;
    }

    try {
      const databases = await indexedDB.databases();
      const deletePromises = databases
        .filter(db => db.name)
        .map(db => new Promise<void>((resolve) => {
          const request = indexedDB.deleteDatabase(db.name!);
          request.onsuccess = () => {
            console.log(`[IndexDB] Deleted database: ${db.name}`);
            resolve();
          };
          request.onerror = () => {
            console.warn(`[IndexDB] Failed to delete: ${db.name}`);
            resolve(); // Don't block on errors
          };
          request.onblocked = () => {
            console.warn(`[IndexDB] Delete blocked for: ${db.name}`);
            setTimeout(resolve, 500);
          };
        }));

      await Promise.all(deletePromises);
      console.log('[IndexDB] All databases deleted');
    } catch (error) {
      console.error('[IndexDB] Error deleting all databases:', error);
    }
  }

  // Legacy methods for backwards compatibility
  deleteIndexDb(): void {
    this.deleteDatabase().catch(err =>
      console.error('[IndexDB] deleteIndexDb error:', err)
    );
  }

  async clearAll(): Promise<boolean> {
    return this.clearAllStores();
  }

  initializeDatabase(): void {
    dbConfig.objectStoresMeta.forEach((storeConfig) => {
      this.dbService.createObjectStore(storeConfig);
    });
  }

  async deleteStore(storeName: Collection): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.deleteObjectStore(storeName));
    } catch (error) {
      console.error(`[IndexDB] Error deleting store ${storeName}:`, error);
      return null;
    }
  }

  async createObjectStore(storeName: Collection): Promise<void> {
    return this.dbService.createObjectStore({
      store: storeName,
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [{ name: 'id', keypath: 'id', options: { unique: true } }]
    });
  }

  async openCursor(storeName: Collection): Promise<any> {
    try {
      return await lastValueFrom(this.dbService.openCursor(storeName));
    } catch (error) {
      console.error(`[IndexDB] Error opening cursor for ${storeName}:`, error);
      return null;
    }
  }

  // Legacy property for backwards compatibility
  get stores(): Collection[] {
    return [...this.STORES];
  }
}
